import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  DateFormatPreference,
  setActiveDateFormat,
  getActiveDateFormat,
} from '@/lib/dateFormat';

const STORAGE_KEY = 'fennest_date_format';

function readCached(): DateFormatPreference | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'uk' || v === 'usa' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Loads the user's date-format preference from their profile and exposes
 * a setter. Side effect: keeps the global active format in sync so
 * non-React code (sorts, helpers) sees the right value.
 */
export function useDateFormatPreference() {
  const { user } = useAuth();
  const [preference, setPreference] = useState<DateFormatPreference>(
    () => readCached() ?? getActiveDateFormat(),
  );
  const [loading, setLoading] = useState(true);

  // Keep global in sync
  useEffect(() => {
    setActiveDateFormat(preference);
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      /* ignore */
    }
  }, [preference]);

  // Hydrate from profile
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('date_format')
          .eq('user_id', user.id)
          .maybeSingle();
        if (cancelled) return;
        const v = (data as any)?.date_format;
        if (v === 'uk' || v === 'usa') setPreference(v);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const updatePreference = useCallback(
    async (next: DateFormatPreference) => {
      setPreference(next);
      if (!user) return { error: null as any };
      const { error } = await supabase
        .from('profiles')
        .update({ date_format: next, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      return { error };
    },
    [user],
  );

  return { preference, updatePreference, loading };
}
